import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FdChipCollectionComponent } from '../fd-chip-collection/fd-chip-collection.component';
import { FilterRequestModel } from 'src/app/models/filter-request.model';
import { AddChipModel } from 'src/app/models/add-chip.model';
import { RegionService } from 'src/app/services/region.service';
import { SelectModel } from 'src/app/models/select.model';
import { MecsFilterSelectOptions } from 'src/app/models/mecs-models/mecs-filter-select-options.model';
import { FilterRequestMecsModel } from 'src/app/models/mecs-models/filter-request-mecs.model';
import { ActivatedRoute } from '@angular/router';
import { MecsRegionFilterSelectOptions } from 'src/app/models/mecs-models/mecs-region-filter-select-options.model';

@Component({
  selector: 'app-mecs-kpi-filter',
  templateUrl: './mecs-kpi-filter.component.html',
  styleUrls: ['./mecs-kpi-filter.component.scss'],
})
export class MecsKpiFilterComponent implements OnInit, OnChanges {
  @ViewChildren(FdChipCollectionComponent)
  fdChipCollectionComponents!: QueryList<FdChipCollectionComponent>;
  @Input() isFiltersRemoved!: boolean;

  @Output() filter = new EventEmitter<FilterRequestMecsModel>();

  selectConfiguration: SelectModel[] = [];

  filterOptions!: MecsFilterSelectOptions;
  filterOptionsRegion!: MecsRegionFilterSelectOptions;

  isFilterContainerOpen = false;
  state: boolean = false;
  filterRequest: FilterRequestMecsModel = {
    startDate: null,
    endDate: null,
    countries: [],
    regions: [],
    solutions: [],
    phases: [],
  };
  regionIdFromUrl: number | null = null;

  constructor(
    private regionService: RegionService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const regionIdParam = params.get('id');
      this.regionIdFromUrl = regionIdParam ? +regionIdParam : null;

      if (this.regionIdFromUrl) {
        this.selectConfiguration = [
          {
            type: 'Country',
            placeholder: 'Select country',
            chipColor: '#E1C3CD',
            options: [],
          },
          {
            type: 'Solution',
            placeholder: 'Select solution',
            chipColor: '#82BCEA',
            options: [],
          },
          {
            type: 'Phase',
            placeholder: 'Select phase',
            chipColor: '#BFADE7',
            options: [],
          },
        ];
      } else {
        this.selectConfiguration = [
          {
            type: 'Region',
            placeholder: 'Select region',
            chipColor: 'rgba(150, 215, 210, 0.70)',
            options: [],
          },
          {
            type: 'Country',
            placeholder: 'Select country',
            chipColor: '#E1C3CD',
            options: [],
          },
          {
            type: 'Solution',
            placeholder: 'Select solution',
            chipColor: '#82BCEA',
            options: [],
          },
          {
            type: 'Phase',
            placeholder: 'Select phase',
            chipColor: '#BFADE7',
            options: [],
          },
        ];
      }
      this.getSelectFilterOptions();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isFiltersRemoved'] &&
      changes['isFiltersRemoved'].currentValue
    ) {
      this.fdChipCollectionComponents.forEach((c) => {
        c.chips = [];
      });
      this.filterRequest = {
        startDate: null,
        endDate: null,
        countries: [],
        regions: [],
        solutions: [],
        phases: [],
      };
    }
  }

  getSelectFilterOptions() {
    if (this.regionIdFromUrl) {
      // Use region-specific API if regionIdFromUrl exists
      this.regionService
        .getMecsRegionFilterOptions(this.regionIdFromUrl)
        .subscribe((response) => {
          this.filterOptionsRegion = response;

          this.selectConfiguration[0].options = response.countries;
          this.selectConfiguration[1].options = response.products;
          this.selectConfiguration[2].options = response.phases.map(
            (p: any) => ({
              id: p.id,
              name: p.name
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .toLowerCase()
                .replace(/\b\w/g, (char: string) => char.toUpperCase()),
            })
          );
        });
    } else {
      // Default to full filter options
      this.regionService.getMecsFilterOptions().subscribe((response) => {
        this.filterOptions = response;

        this.selectConfiguration[0].options = response.regionCountrySelect.map(
          (x) => ({
            id: x.id,
            name: x.name,
          })
        );
        this.selectConfiguration[2].options = response.products;
        this.selectConfiguration[3].options = response.phases.map((p: any) => ({
          id: p.id,
          name: p.name
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            .toLowerCase()
            .replace(/\b\w/g, (char: string) => char.toUpperCase()),
        }));
      });
    }
  }

  onClick(): void {
    this.isFilterContainerOpen = !this.isFilterContainerOpen;
    this.state = this.state === false ? true : false;
  }

  emitFilterRequestModel() {
    this.filterRequest.countries = [];
    this.filterRequest.regions = [];
    this.filterRequest.solutions = [];
    this.filterRequest.phases = [];

    this.fdChipCollectionComponents.forEach((c) => {
      if (c.type?.toLowerCase() === 'country') {
        this.filterRequest.countries.push(...c.chips.map((x) => x.id));
      } else if (c.type?.toLowerCase() === 'region') {
        this.filterRequest.regions.push(...c.chips.map((x) => x.id));
      } else if (c.type?.toLowerCase() === 'solution') {
        this.filterRequest.solutions.push(...c.chips.map((x) => x.id));
      } else if (c.type?.toLowerCase() === 'phase') {
        this.filterRequest.phases.push(...c.chips.map((x) => x.id));
      }
    });

    if (this.regionIdFromUrl) {
      this.filterRequest.regions = [this.regionIdFromUrl];
    }

    this.filter.emit(this.filterRequest);
  }

  removeChip(e: any): void {
    this.selectConfiguration[1].options = [];

    const selectedRegionIds: number[] = [];

    this.fdChipCollectionComponents.forEach((c) => {
      if (c.type?.toLowerCase() === 'region') {
        selectedRegionIds.push(...c.chips.map((chip) => Number(chip.id)));
      }
    });

    selectedRegionIds.forEach((regionId) => {
      const region = this.filterOptions.regionCountrySelect.find(
        (r) => r.id === regionId
      );
      if (region?.countries) {
        this.selectConfiguration[1].options.push(...region.countries);
      }
    });

    const uniqueMap = new Map();
    this.selectConfiguration[1].options =
      this.selectConfiguration[1].options.filter((country) => {
        if (!uniqueMap.has(country.id)) {
          uniqueMap.set(country.id, true);
          return true;
        }
        return false;
      });
  }

  callAddChipMethod(data: AddChipModel): void {
    const collection = this.fdChipCollectionComponents.find(
      (x) => x.type === data.collectionType
    );

    collection?.addChip(data);

    const countryConfig = this.selectConfiguration.find(
      (cfg) => cfg.type === 'Country'
    );
    const solutionConfig = this.selectConfiguration.find(
      (cfg) => cfg.type === 'Solution'
    );

    if (!countryConfig || !solutionConfig) return;

    // Reset country options
    countryConfig.options = [];

    if (this.regionIdFromUrl && this.filterOptionsRegion?.countries) {
      // 👇 Use countries from region-specific filter options
      countryConfig.options = this.filterOptionsRegion.countries;
    } else {
      // 👇 Original behavior for multi-region scenario
      const selectedRegionIds: number[] = [];

      this.fdChipCollectionComponents.forEach((c) => {
        if (c.type?.toLowerCase() === 'region') {
          selectedRegionIds.push(
            ...c.chips.map((chip) => Number(chip.id)).filter((id) => !isNaN(id))
          );
        }
      });

      selectedRegionIds.forEach((regionId) => {
        const region = this.filterOptions.regionCountrySelect.find(
          (r) => r.id === regionId
        );
        if (region?.countries) {
          countryConfig.options.push(...region.countries);
        }
      });

      // Ensure unique countries
      const uniqueMap = new Map();
      countryConfig.options = countryConfig.options.filter((country) => {
        if (!uniqueMap.has(country.id)) {
          uniqueMap.set(country.id, true);
          return true;
        }
        return false;
      });
    }

    // Always reassign solution options
    if (this.regionIdFromUrl && this.filterOptionsRegion?.products) {
      solutionConfig.options = this.filterOptionsRegion.products;
    } else if (this.filterOptions?.products) {
      solutionConfig.options = this.filterOptions.products;
    }
  }

  setDateRange(range: any) {
    this.filterRequest.startDate = range.startDate;
    this.filterRequest.endDate = range.endDate;
  }
}
